import * as zod from 'zod';

/**
 * The Inspect step's output: one detection count per defect class, plus the two
 * capture variables it shares with Locate.
 *
 * The counts are `count_<id>` keys at the TOP LEVEL, because the editor derives
 * a condition's identifier from the variable name and builds a property access
 * (`variables['<stepId>'].<name>`) — brackets break the free-text expression
 * editor, so a nested `countsByClass` record cannot be branched on.
 *
 * THE KEY TYPE IS A TEMPLATE LITERAL, and that is load-bearing rather than
 * decorative. A plain `[k: string]` index signature — which is what
 * `zod.infer` gives a catchall — makes this union member a candidate for
 * TypeScript's `'prop' in value` narrowing against ANY property name. Several
 * places narrow `TaggedRoutineStepVariables` structurally instead of on
 * `stepKind` (`RoutineFrame.getVariableByName` on `'name'`,
 * `RoutineFrame.setVariable` on `'isGlobal'`, `ResumeFromStepModal` on
 * `'completedCount'`), and a bare index signature silently widens each of those
 * properties to `unknown`. Two of them are truthiness or `===` checks, which
 * accept `unknown` without complaint — so the guarantee would disappear with no
 * error anywhere. Constraining the key to `count_${string}` keeps this member
 * out of that narrowing, because no unrelated property name is assignable to it.
 *
 * `captureTimestamp` and `latestSaveFilepath` are named members alongside it,
 * which does NOT reopen that hazard: the three structural checks above are on
 * `'name'`, `'isGlobal'` and `'completedCount'`, and none of those is
 * assignable to a `count_${string}` key or to either name here. They carry the
 * same types Locate and RunLocateSkill declare, so a routine reads the frame an
 * Inspect step wrote exactly the way it reads a Locate one.
 */
export type InspectStepVariables = {
  [countKey: `count_${string}`]: number;
  captureTimestamp: string | null;
  latestSaveFilepath: string | null;
};

/**
 * Runtime stays permissive where the declared type does not.
 *
 * The catchall has to be `unknown` at run time: `RoutineStepVariables.tag()`
 * wraps every step's Variables in a zod INTERSECTION with
 * `{ stepKind: literal }`, and an intersection parses the same input with both
 * sides — so under a `number` catchall the literal `stepKind` string reaches
 * this schema as an unknown key and fails on its own value.
 * `__tests__/Variables.test.ts` pins that.
 *
 * The cast narrows only what callers SEE. This step is the schema's sole
 * writer, and it writes exactly the declared shape — a `count_<id>: number`
 * per configured class, plus the two capture variables — so the declared type
 * is the honest description of the data; the looser runtime shape exists to
 * satisfy `tag()`, not to admit other content.
 *
 * The capture pair stays OUT of the zod object even though the declared type
 * requires it. Adding them there makes them required at parse time, and the
 * pinned tests parse a tagged `{ stepKind }` with no variables at all — which
 * is what a step reads before its first play. The catchall already admits both
 * keys, so the runtime shape needs no change to carry them.
 */
const Variables = zod
  .object({})
  .catchall(zod.unknown()) as unknown as zod.ZodSchema<InspectStepVariables>;

export default Variables;
